import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiChevronDown, mdiChevronRight, mdiLockOutline, mdiOpenInNew, mdiTrashCanOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { apiBaseUrl } from '../../api/client'
import { useDocument } from '../../hooks/useQueries'
import { DocumentPreview, getFileTypeIcon } from './DocumentPreview'
import { SignedFlagBadges } from './SignedFlagBadges'

export interface DocumentPreviewCardDoc {
  uid?: string
  name?: string
  file_name?: string
  file_mime_type?: string | null
  /** Row markers — `conversation_private` draws the lock (spec 2026-09-12-01). */
  flags?: string[] | null
  /** Extracted text — a recording's transcript, shown under its player. */
  text_content?: string | null
}

interface DocumentPreviewCardProps {
  org: string
  documentUid: string
  /** Optional pre-fetched document — avoids a redundant request when the
   * host already embeds it (e.g. InterventionDocument.document). Falls back
   * to useDocument when absent or missing a mime type. */
  document?: DocumentPreviewCardDoc | null
  defaultOpen?: boolean
  /** Optional trailing icon-only action, rendered next to the open-document
   * link. The card never detaches on its own — the host decides what
   * "detach" means (direct mutation, confirmation dialog, etc). */
  onDetach?: () => void
  /** Prefix for every data-testid this component renders. */
  testidPrefix: string
  className?: string
}

/**
 * Collapsible, self-contained preview of a single document — meant to be
 * embedded on any entity page that lists attached documents (interventions,
 * expenses, contracts). Collapsed by default; the iframe/img/video is not
 * mounted until the panel has been opened at least once, so a page with
 * several attached documents doesn't fetch every blob on load.
 */
export function DocumentPreviewCard({
  org,
  documentUid,
  document: providedDoc,
  defaultOpen = false,
  onDetach,
  testidPrefix,
  className,
}: DocumentPreviewCardProps) {
  const { t } = useTranslation('documents')
  const [open, setOpen] = useState(defaultOpen)
  const [everOpened, setEverOpened] = useState(defaultOpen)

  const needsFetch = !providedDoc?.file_mime_type
  const { data: fetchedDoc } = useDocument(org, needsFetch ? documentUid : '')
  const doc: DocumentPreviewCardDoc | undefined = providedDoc?.file_mime_type
    ? providedDoc
    : (fetchedDoc as DocumentPreviewCardDoc | undefined) || providedDoc || undefined

  const displayName = doc?.name || doc?.file_name || documentUid.slice(0, 8)
  const mimeType = doc?.file_mime_type
  // A file still private to its conversation: the manager must see, on the chip
  // itself, that this one has not reached the organization's referential
  // (spec 2026-09-12-01 § 3). Read off whichever copy of the row we have —
  // the embedded one in a thread payload, or the fetched one.
  const conversationPrivate =
    (providedDoc?.flags ?? (doc as DocumentPreviewCardDoc | undefined)?.flags)?.includes(
      'conversation_private',
    ) ?? false

  const rowFlags = providedDoc?.flags ?? (doc as DocumentPreviewCardDoc | undefined)?.flags

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) setEverOpened(true)
  }

  return (
    <div
      className={`rounded-md border border-border ${className || ''}`}
      data-testid={testidPrefix}
    >
      <Collapsible open={open} onOpenChange={handleOpenChange}>
        <div className="flex items-center gap-1 px-3 py-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
              data-testid={`${testidPrefix}-toggle`}
              aria-label={open ? t('preview_card.collapse') : t('preview_card.expand')}
              title={open ? t('preview_card.collapse') : t('preview_card.expand')}
            >
              <AppIcon tone="muted" path={open ? mdiChevronDown : mdiChevronRight} size="sm" className="shrink-0" />
              <AppIcon tone="muted" path={getFileTypeIcon(mimeType)} size="sm" className="shrink-0" />
              <span className="truncate font-medium text-foreground">{displayName}</span>
              {conversationPrivate && (
                <AppIcon
                  path={mdiLockOutline}
                  size="xs"
                  className="shrink-0 text-amber-600 dark:text-amber-400"
                  aria-label={t('conversation_private.locked')}
                  title={t('conversation_private.locked')}
                  data-testid={`${testidPrefix}-private`}
                />
              )}
            </button>
          </CollapsibleTrigger>
          <SignedFlagBadges flags={rowFlags} testidPrefix={testidPrefix} />
          <Link
            to="/$org/documents/$docUid"
            params={{ org, docUid: documentUid }}
            className="shrink-0 rounded p-1.5 text-muted-foreground hover:text-primary hover:bg-muted"
            aria-label={t('preview_card.open_document')}
            title={t('preview_card.open_document')}
            data-testid={`${testidPrefix}-link`}
          >
            <AppIcon path={mdiOpenInNew} size="sm" />
          </Link>
          {onDetach && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={onDetach}
              aria-label={t('preview_card.detach')}
              title={t('preview_card.detach')}
              data-testid={`${testidPrefix}-detach`}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          )}
        </div>
        <CollapsibleContent>
          <div className="border-t border-border px-3 py-3" data-testid={`${testidPrefix}-preview`}>
            {everOpened ? (
              <DocumentPreview
                mimeType={mimeType}
                name={displayName}
                contentUrl={`${apiBaseUrl}/api/v1/organizations/${org}/documents/${documentUid}/content`}
                previewUrl={`${apiBaseUrl}/api/v1/organizations/${org}/documents/${documentUid}/thumb`}
                maxHeight={400}
                bare
                transcript={doc?.text_content}
              />
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
